import React, { useEffect, useState } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Footer from './Footer';

interface LayoutProps {
  children: React.ReactNode;
}

export default function Layout({ children }: LayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { email, signOut } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => {
    await signOut();
    navigate('/auth');
  };

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const navigation = [
    { label: 'Dashboard', to: '/' },
    { label: 'Orders', to: '/receipts' },
    { label: 'Price History', to: '/price-history' },
    { label: 'Analysis', to: '/analysis' },
    { label: 'Settings', to: '/settings' },
  ];

  const navClassName = ({ isActive }: { isActive: boolean }) =>
    isActive ? 'pill-tab pill-tab-active' : 'pill-tab';

  return (
    <div className="min-h-screen bg-transparent flex flex-col">
      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-ink-100/70 bg-white/75 backdrop-blur-xl">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="inline-flex items-center gap-2">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-100 text-primary-600 font-bold">
                  CC
                </span>
                <span className="text-xl font-semibold text-ink-700">CrossCheckout</span>
              </Link>
            </div>

            <div className="flex items-center gap-4">
              <div className="hidden lg:flex items-center gap-2">
                {navigation.map((item) => (
                  <NavLink key={item.to} to={item.to} className={navClassName} end={item.to === '/'}>
                    {item.label}
                  </NavLink>
                ))}
              </div>

              <button
                type="button"
                onClick={() => setMenuOpen((value) => !value)}
                className="btn-ghost lg:hidden"
                aria-label="Toggle navigation menu"
                aria-expanded={menuOpen}
              >
                <span className="text-lg">{menuOpen ? '✕' : '☰'}</span>
              </button>

              <div className="hidden sm:flex items-center gap-3 rounded-full border border-white/60 bg-white/60 px-4 py-2 shadow-soft backdrop-blur">
                <span className="text-xs uppercase tracking-wide text-ink-400">Signed in</span>
                <span className="text-sm font-medium text-ink-600 truncate max-w-[160px]">{email}</span>
                <button onClick={handleLogout} className="btn-ghost text-xs font-semibold text-primary-600">
                  Logout
                </button>
              </div>
            </div>
          </div>

          {menuOpen && (
            <div className="lg:hidden pb-4">
              <div className="space-y-2 rounded-2xl border border-ink-100/80 bg-white/85 p-3 shadow-soft backdrop-blur">
                {navigation.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `${isActive ? 'pill-tab-active bg-primary-50 text-primary-600' : 'text-ink-500'} block rounded-xl px-4 py-3 text-sm font-semibold`
                    }
                    end={item.to === '/'}
                  >
                    {item.label}
                  </NavLink>
                ))}
                <div className="flex flex-col gap-2 border-t border-ink-100/60 pt-3">
                  <span className="text-xs uppercase tracking-wide text-ink-400">Signed in as</span>
                  <span className="text-sm font-medium text-ink-600">{email}</span>
                  <button onClick={handleLogout} className="btn-primary justify-center">
                    Logout
                  </button>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto w-full flex-1 px-4 sm:px-6 lg:px-8 py-12">
        {children}
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
}
